interface ExportToolbarButtonProps {
  onClick: () => void;
  /**
   * When true the button renders disabled with a sr-only reason describing
   * why (typically "no successful run yet"). The page-level wiring computes
   * this off `latestRun`.
   */
  disabled?: boolean;
  /** Tooltip / aria-describedby copy when disabled. */
  disabledReason?: string;
}

/**
 * Header toolbar button that opens the Phase 6 Export modal. Visually mirrors
 * the Rooms / Subjects / Course Requests buttons so the toolbar reads as a
 * single row of equivalent actions. The schedule home page is responsible for
 * gating mount on the `canManageScheduleMasterData` role (matching every other
 * admin-only header action) and for setting `disabled` when no latest run is
 * available — the modal targets a specific run, so there's nothing to export
 * before the first successful scheduler run.
 */
export function ExportToolbarButton({
  onClick,
  disabled = false,
  disabledReason,
}: ExportToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      data-testid="schedule-export-button"
      aria-label="Export schedule"
      className="bg-surface-2 text-text-soft rounded-md px-[14px] py-[7px] font-medium text-[13px] cursor-pointer flex items-center gap-1.5 hover:bg-surface-2/80 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="M7 10l5 5 5-5" />
        <path d="M12 15V3" />
      </svg>
      Export
    </button>
  );
}
